import { setMessageOverrides } from "@clubcore/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useLocale, useWordingTools } from "../../i18n/I18nProvider";
import { useClub } from "../club";
import { useApi } from "../services";

/** Everything the wording tools cache for one club starts with this, so one invalidation refreshes all of it. */
export const wordingScope = (slug: string) => [slug, "wording"] as const;

/**
 * Keeps the staff app's message runtime in step with the club's wording: fetches the effective overrides for the
 * interface language (the browser revalidates by ETag, so an unchanged wording costs a 304) and applies them. A failure leaves
 * the built-in text in place; wording is never worth an error banner.
 */
export function useWordingSync(): void {
  const api = useApi();
  const { club } = useClub();
  const { locale } = useLocale();
  const { refresh } = useWordingTools();
  const query = useQuery({
    queryKey: [...wordingScope(club.slug), "effective", locale],
    queryFn: ({ signal }) => api.wording.effective("staff", locale, signal),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
  const version = query.data?.version;
  useEffect(() => {
    if (!query.data) return;
    setMessageOverrides(query.data.locale, query.data.overrides);
    refresh();
    // `version` stands for the contents: the same wording fetched again must not redraw the whole app
  }, [version, query.data, refresh]);
  useEffect(
    () => () => {
      // signing out or leaving the club: another club's wording must never show here
      setMessageOverrides("en", undefined);
      setMessageOverrides("es", undefined);
    },
    [club.slug],
  );
}
